
import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import logger from '@/utils/logger';

interface RealtimeSubscriptionOptions {
  /**
   * Filtro `postgres_changes` (es. `list_id=eq.<uuid>`). Limita gli eventi
   * realtime alla sola riga/lista attiva: senza filtro la subscription
   * riceverebbe ogni cambiamento della tabella anche per liste non visibili
   * (vedi issue #1422 / regola CLAUDE.md "Realtime solo per liste attive").
   */
  filter?: string;
  /**
   * Quando `false` non viene aperto alcun canale: evita una subscription
   * globale di background quando non c'è una lista attiva su cui filtrare.
   */
  enabled?: boolean;
}

export const useRealtimeSubscription = <T>(
  table: string,
  callback: (payload: any) => void,
  options: RealtimeSubscriptionOptions = {}
) => {
  const { filter, enabled = true } = options;
  const channelRef = useRef<any>(null);

  useEffect(() => {
    // Niente lista attiva su cui filtrare → nessun canale (no background globale)
    if (!enabled) {
      return;
    }

    // Nome canale univoco per filtro: evita collisioni quando si cambia lista
    const channelName = `${table}-changes-${filter ?? 'all'}`;

    // Create a channel for realtime updates
    const realtimeChannel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table,
          ...(filter ? { filter } : {})
        },
        (payload) => {
          callback(payload);
        }
      )
      .subscribe((status) => {
        logger.log('Realtime subscription status:', status);
      });

    channelRef.current = realtimeChannel;

    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(realtimeChannel);
      channelRef.current = null;
    };
  }, [table, callback, filter, enabled]);

  return channelRef.current;
};
